import { Injectable } from '@angular/core';

import { PeopleContentService } from '@alfresco/adf-content-services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  filter,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import * as _ from 'lodash';

import { DecoratorsService } from '../services/decorators.service';
import { SuggestionCleanupService } from '../services/suggestion-cleanup.service';
import {
  AppStore,
  getCurrentDocumentId,
  SnackbarErrorAction,
} from '../../../store';

import * as DecoratorsActions from './decorators.actions';
import { CitationModel, ITrackChange, IUserInfo } from '../models';

import { DecoratorsTrackChangeConstant as DTC } from '../const';
import { WkTemplate } from '../../../model/wk-template-model';
import {
  CitationEntry,
  ValidationResults,
} from '../../../model/validation-service.model';
import { getTrackChange } from './selectors/get.trackchange';
import {
  getNumberedCitations,
  getCitationStack,
  getUndoStackIndex,
  getUnnumberedCitations,
  getUnNumberedCitationStack,
  getUnNumRefUndoStackIndex,
} from './selectors';
import { CustomContentManagementService } from '../../../services/custom-content-management.service';
import {
  downloadValidationList,
  setDocumentValidationStatus,
} from './decorators.actions';

const VALIDATION_RESULTS_KEY = 'apollo:validationResults';
const VALIDATION_STATUS_KEY = 'apollo:validationStatus';

@Injectable()
export class DecoratorsEffects {
  // #region Properties (4)

  public initDecoratorsLoading$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DecoratorsActions.initDecoratorsLoading),
      switchMap(() =>
        this.getCurrentNode().pipe(
          filter((nodeId): nodeId is string => !!nodeId),
          switchMap((nodeId) =>
            of(
              DecoratorsActions.setCurrentDocumentId({
                nodeId,
              }),
              DecoratorsActions.loadingAllDecorators({ nodeId })
            )
          )
        )
      )
    )
  );

  public loadAllDecorators$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DecoratorsActions.loadingAllDecorators),
      switchMap((action: { type: Required<string>; nodeId?: string }) =>
        this.loadDecorators(action?.nodeId).pipe(
          map((values) => {
            this.setContentMetaData(values);
            return DecoratorsActions.loadAllDecoratorsCompleted({
              ...values,
            });
          }),
          catchError((err) => {
            this.showError(err);
            return of(DecoratorsActions.loadingAllDecoratorsFailed());
          })
        )
      )
    )
  );

  public setEditorDependency$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DecoratorsActions.setEditorDependency),
        switchMap(() =>
          this.store.select(getTrackChange).pipe(
            take(1),
            tap((trackChangeData) => {
              const currentTrackChangeData = localStorage.getItem(
                DTC.TRACK_CHANGES_AND_COMMENTS_DATA
              );

              if (currentTrackChangeData) {
                try {
                  const parsedTrackChangeData = JSON.parse(
                    currentTrackChangeData
                  );
                  if (
                    !_.isEqual(
                      currentTrackChangeData,
                      JSON.stringify(trackChangeData)
                    )
                  ) {
                    const uniqueSuggestionsForRHP = _.filter(
                      [
                        ...trackChangeData.suggestions,
                        ...parsedTrackChangeData?.suggestions,
                      ],
                      (item, index, self) =>
                        index === _.findIndex(self, (t) => t.id === item.id)
                    );

                    const uniqueCommentThreads = _.filter(
                      [
                        ...trackChangeData.commentThreads,
                        ...parsedTrackChangeData?.commentThreads,
                      ],
                      (item, index, self) =>
                        index ===
                        _.findIndex(self, (t) => t.threadId === item.threadId)
                    );
                    trackChangeData.suggestions = [...uniqueSuggestionsForRHP];

                    trackChangeData.commentThreads = [...uniqueCommentThreads];
                  }
                } catch (e) {
                  console.error(
                    'Failed to parse track change data from localStorage',
                    e
                  );
                }
              }
              localStorage.setItem(
                DTC.TRACK_CHANGES_AND_COMMENTS_DATA,
                JSON.stringify(trackChangeData)
              );
            })
          )
        )
      ),
    { dispatch: false }
  );

  public onLoadingReadonlyContent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DecoratorsActions.onLoadingReadonlyContent),
      switchMap(
        (action: {
          type: Required<string>;
          id: Required<string>;
          url: Required<string>;
        }) =>
          this.loadMetaData(action.id).pipe(
            map((values) => {
              this.setContentMetaData(values);
              return DecoratorsActions.onLoadCompleteReadonlyContent({
                ...values,
                loaded: true,
                citations: values.citations,
              });
            }),
            catchError((err) => {
              this.showError(err);
              return of(
                DecoratorsActions.onLoadCompleteReadonlyContent({
                  trackChangeData: {
                    commentThreads: [],
                    suggestions: [],
                  },
                  docContent: '',
                  loaded: false,
                  citations: { unnumbered_references: [], references: [] },
                })
              );
            })
          )
      )
    )
  );

  public loadDocumentAndContent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DecoratorsActions.loadDocumentAndContent),
      switchMap(
        (action: {
          type: Required<string>;
          id: Required<string>;
          url: Required<string>;
        }) =>
          this.loadDecorators(action.id).pipe(
            map((values) => {
              this.setContentMetaData(values);
              return DecoratorsActions.loadDocumentAndContentComplete({
                ...values,
              });
            })
          )
      )
    )
  );

  // #endregion Properties (4)

  // #region Constructors (1)

  constructor(
    private readonly actions$: Actions,
    private readonly customContentService: CustomContentManagementService,
    private readonly service: DecoratorsService,
    private readonly store: Store<AppStore>,
    private readonly ecmUserService: PeopleContentService,
    private readonly suggestionCleanupService: SuggestionCleanupService
  ) {}

  // #endregion Constructors (1)

  // #region Private Methods (3)

  private setContentMetaData(values) {
    localStorage.setItem('currentUser', JSON.stringify(values.currentUser));
    values.trackChangeData = _.isEmpty(values.trackChangeData)
      ? {
          commentThreads: [],
          suggestions: [],
        }
      : values.trackChangeData;
    localStorage.setItem(
      DTC.TRACK_CHANGES_AND_COMMENTS_DATA,
      JSON.stringify(values.trackChangeData)
    );
    localStorage.setItem(
      DTC.TRACK_CHANGES_AND_COMMENTS_INVOLVED_USERS,
      JSON.stringify(values.usersInfo)
    );

    this.processAndDispatchValidationStatus(values);
  }

  /**
   * Extracts validation results from nested backend response, flattens citation arrays,
   * prefixes each entry with its validation type, keeps backend status as-is,
   * and dispatches the merged results to the store.
   */
  private processAndDispatchValidationStatus(values): void {
    if (!values?.documentValidationStatus) {
      return;
    }

    const currentValidationResults =
      values.documentValidationStatus?.currentValidationResults;

    const validationSources: Array<{
      type: string;
      results: ValidationResults[];
    }> = [
      {
        type: 'HTML',
        results:
          currentValidationResults?.html5?.[VALIDATION_RESULTS_KEY] ?? [],
      },
      {
        type: 'NUMBERED CITATIONS',
        results: this.flattenCitationResults(
          currentValidationResults?.citations?.numbered
        ),
      },
      {
        type: 'UNNUMBERED CITATIONS',
        results: this.flattenCitationResults(
          currentValidationResults?.citations?.unnumbered
        ),
      },
    ];

    // Tag each entry with its validation type for downstream grouping
    const mergedValidationResults = validationSources.reduce<
      ValidationResults[]
    >(
      (acc, { type, results }) =>
        acc.concat(results.map((entry) => ({ ...entry, type }))),
      []
    );

    // Top-level currentValidationStatus is the aggregate; prefer it over section-level status
    const rawStatus =
      values.documentValidationStatus?.currentValidationStatus ||
      currentValidationResults?.html5?.[VALIDATION_STATUS_KEY] ||
      'NOT_VALIDATED';

    this.store.dispatch(
      setDocumentValidationStatus({
        documentValidationStatus: {
          status: rawStatus,
          validationResults: mergedValidationResults,
        },
      })
    );
  }

  /**
   * Flattens nested citation validation results into a single array.
   */
  private flattenCitationResults(
    citations: CitationEntry[] = []
  ): ValidationResults[] {
    return (citations || []).reduce<ValidationResults[]>(
      (acc, entry) => acc.concat(entry?.[VALIDATION_RESULTS_KEY] ?? []),
      []
    );
  }

  downloadValidationList$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(downloadValidationList),
        map(() => this.customContentService.downloadValidationList())
      ),
    { dispatch: false }
  );

  private getCurrentNode(): Observable<string | undefined> {
    return this.store.select(getCurrentDocumentId);
  }

  private loadDecorators(nodeId?: string): Observable<{
    citations: CitationModel;
    documentContent: string | null;
    trackChangeData: ITrackChange;
    subTemplates: WkTemplate[];
    keywords: any[];
    usersInfo: IUserInfo[];
    currentUser: IUserInfo;
  }> {
    return nodeId
      ? this.service.getOrchestratedData(nodeId).pipe(
          take(1),
          map((data) => {
            // Filter valid suggestions that exist in content
            const validSuggestions = _.filter(data.suggestions, (suggestion) =>
              data.content.includes(suggestion.id)
            );

            // Clean up orphaned suggestion markers from content
            const cleanedContent =
              this.suggestionCleanupService.cleanupOrphanedSuggestionMarkers(
                data.content,
                validSuggestions
              );

            const rhpSuggestions = _.filter(
              data.suggestions,
              (s) => s.isRHPSuggestion
            );

            const cleanupCitationStrings = (refs) =>
              _.map(refs, (ref) => {
                ref.citationString =
                  this.suggestionCleanupService.cleanupOrphanedSuggestionMarkers(
                    ref.citationString,
                    rhpSuggestions
                  );
                return ref;
              });
            // Santizes references and unnumbered references from corrupted trackchanges
            // Prevents track changes missing suggestions error in RHP when loading document with corrupted track changes
            data.citations.references = cleanupCitationStrings(
              data.citations.references
            );
            data.citations.unnumbered_references = cleanupCitationStrings(
              data.citations.unnumbered_references
            );

            return {
              citations: _.isEmpty(data.citations)
                ? {
                    unnumbered_references: [],
                    references: [],
                    fragmentLinks: {
                      incomingLinks: [],
                      outgoingLinks: [],
                    },
                  }
                : {
                    ...data.citations,
                    fragmentLinks: { incomingLinks: [], outgoingLinks: [] },
                  },
              documentContent: cleanedContent,
              trackChangeData: {
                suggestions: [
                  ...validSuggestions,
                  ..._.filter(data.suggestions, (s) => s.isRHPSuggestion),
                ],
                commentThreads: data.commentThreads?.length
                  ? data.commentThreads
                  : [],
              },
              subTemplates: data.subTemplates,
              keywords: data.keyWords,
              usersInfo: _.map(data.authors, (value) => {
                const userInfo = {
                  id: value.id,
                  name: value.displayName,
                };
                if (value.avatarId) {
                  userInfo['avatar'] = this.ecmUserService.getUserProfileImage(
                    value.avatarId
                  );
                }
                return userInfo;
              }),
              currentUser: {
                id: data.currentUser?.id,
                name: data.currentUser?.displayName,
                ...(data.currentUser?.avatarId && {
                  avatar: this.ecmUserService.getUserProfileImage(
                    data.currentUser?.avatarId
                  ),
                }),
              },
              documentValidationStatus: data.documentValidationStatus,
            };
          })
        )
      : of({
          citations: {
            unnumbered_references: [],
            references: [],
            fragmentLinks: {
              incomingLinks: [],
              outgoingLinks: [],
            },
          },
          documentContent: '',
          trackChangeData: {
            suggestions: [],
            commentThreads: [],
          },
          keywords: [],
          subTemplates: [],
          usersInfo: [],
          currentUser: {
            id: '',
            name: '',
          },
        });
  }

  private showError(err: Error) {
    try {
      this.store.dispatch(
        new SnackbarErrorAction(
          JSON.parse(err.message)?.error?.briefSummary ||
            'APP.MESSAGES.ERRORS.DOCUMENT_AND_META_FETCH'
        )
      );
    } catch (ex) {
      throw Error(ex instanceof Error ? ex.message : String(ex));
    }
  }

  private loadMetaData(nodeId: string): Observable<{
    trackChangeData: ITrackChange;
    docContent: string;
    citations: CitationModel;
    usersInfo: IUserInfo[];
    currentUser: IUserInfo;
  }> {
    return this.service.getOrchestratedData(nodeId).pipe(
      take(1),
      map((data) => ({
        citations: _.isEmpty(data.citations)
          ? {
              unnumbered_references: [],
              references: [],
              fragmentLinks: {
                incomingLinks: [],
                outgoingLinks: [],
              },
            }
          : {
              ...data.citations,
              fragmentLinks: { incomingLinks: [], outgoingLinks: [] },
            },
        docContent: data.content,
        trackChangeData: {
          suggestions: data.suggestions?.length ? data.suggestions : [],
          commentThreads: data.commentThreads?.length
            ? data.commentThreads
            : [],
        },
        usersInfo: _.map(data.authors, (value) => {
          const userInfo = {
            id: value.id,
            name: value.displayName,
          };
          if (value.avatarId) {
            userInfo['avatar'] = this.ecmUserService.getUserProfileImage(
              value.avatarId
            );
          }
          return userInfo;
        }),
        currentUser: {
          id: data.currentUser?.id,
          name: data.currentUser?.displayName,
          ...(data.currentUser?.avatarId && {
            avatar: this.ecmUserService.getUserProfileImage(
              data.currentUser?.avatarId
            ),
          }),
        },
      }))
    );
  }

  public createNewCitationStackEntry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DecoratorsActions.updateNumberedReferences),
      withLatestFrom(
        this.store.select(getNumberedCitations),
        this.store.select(getCitationStack),
        this.store.select(getUndoStackIndex)
      ),
      map(([_action, references, citationStack, undoStackIndex]) => {
        /** If user is inbetween two already undo'ed actions and creates a new insertion of numbered reference
         * Then, state.citationStack will be sliced from current state.undoStackIndex
         * Then, state.citationStack.unshift happens with the new numbered reference
         */

        if (undoStackIndex) {
          citationStack = _.cloneDeep(citationStack.slice(undoStackIndex));
          undoStackIndex = 0;
        }
        /**
         * Avoiding a continous emprty array added into Stack,
         * Scenatio : Discard All the multiple Insertion citation it'll remove from all,
         * but in stack it'll add one by one that will cause issue with undo fetch.
         */
        const isNotEmptyStack =
          references.length !== 0 || citationStack?.[0]?.length !== 0;

        const isDuplicateStack = _.isEqual(
          JSON.stringify(references),
          JSON.stringify(citationStack?.[0])
        );
        isNotEmptyStack &&
          !isDuplicateStack &&
          citationStack.unshift(_.cloneDeep(references));

        return DecoratorsActions.updateCitationsInRHPWithTrackchangeData({
          citationStack,
          undoStackIndex,
        });
      })
    )
  );

  public updateCitationStackWithTrackChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DecoratorsActions.updateNumberedReferencesWithoutUndo),
      withLatestFrom(
        this.store.select(getNumberedCitations),
        this.store.select(getCitationStack),
        this.store.select(getUndoStackIndex)
      ),
      map(([_action, references, citationStack, undoStackIndex]) => {
        const updatedCitationStack = citationStack.map((stack) =>
          stack.map((refInsideEachStack) => {
            const citationObj = references.find(
              (r) => r.citationId === refInsideEachStack.citationId
            );
            return citationObj
              ? {
                  ...refInsideEachStack,
                  citationString: citationObj.citationString,
                }
              : refInsideEachStack;
          })
        );
        return DecoratorsActions.updateCitationsInRHPWithTrackchangeData({
          citationStack: updatedCitationStack,
          undoStackIndex,
        });
      })
    )
  );

  public createNewUnNumberedCitationStackEntry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DecoratorsActions.updateUnnumberedReferences),
      withLatestFrom(
        this.store.select(getUnnumberedCitations),
        this.store.select(getUnNumberedCitationStack),
        this.store.select(getUnNumRefUndoStackIndex)
      ),
      map(
        ([
          _action,
          unnumberedReferences,
          unnumberedCitationStack,
          unNumRefUndoStackIndex,
        ]) => {
          /** If user is inbetween two already undo'ed actions and creates a new insertion of numbered reference
           * Then, state.citationStack will be sliced from current state.undoStackIndex
           * Then, state.citationStack.unshift happens with the new numbered reference
           */

          if (unNumRefUndoStackIndex) {
            unnumberedCitationStack = _.cloneDeep(
              unnumberedCitationStack.slice(unNumRefUndoStackIndex)
            );
            unNumRefUndoStackIndex = 0;
          }
          /**
           * Avoiding a continous emprty array added into Stack,
           * Scenatio : Discard All the multiple Insertion citation it'll remove from all,
           * but in stack it'll add one by one that will cause issue with undo fetch.
           */
          const isNotEmptyStack =
            unnumberedReferences.length !== 0 ||
            unnumberedCitationStack[0].length !== 0;

          const isDuplicateStack = _.isEqual(
            JSON.stringify(unnumberedReferences),
            JSON.stringify(unnumberedCitationStack[0])
          );
          isNotEmptyStack &&
            !isDuplicateStack &&
            unnumberedCitationStack.unshift(_.cloneDeep(unnumberedReferences));

          return DecoratorsActions.updateUnNumberedCitationsInRHPWithTrackchangeData(
            { citationStack: unnumberedCitationStack, unNumRefUndoStackIndex }
          );
        }
      )
    )
  );

  public updateUnNumberedCitationStackWithTrackChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DecoratorsActions.updateUnNumberedReferencesWithoutUndo),
      withLatestFrom(
        this.store.select(getUnnumberedCitations),
        this.store.select(getUnNumberedCitationStack),
        this.store.select(getUnNumRefUndoStackIndex)
      ),
      map(([_action, references, citationStack, unNumRefUndoStackIndex]) => {
        const updatedCitationStack = citationStack.map((stack) =>
          stack.map((refInsideEachStack) => {
            const citationObj = references.find(
              (r) => r.citationId === refInsideEachStack.citationId
            );
            return citationObj
              ? {
                  ...refInsideEachStack,
                  citationString: citationObj.citationString,
                }
              : refInsideEachStack;
          })
        );

        return DecoratorsActions.updateUnNumberedCitationsInRHPWithTrackchangeData(
          { citationStack: updatedCitationStack, unNumRefUndoStackIndex }
        );
      })
    )
  );

  // #endregion Private Methods (3)
}
